import { Field, FieldProps, Form, Formik, FormikProps } from 'formik'
import * as React from 'react'
import * as Yup from 'yup'
import * as classNames from 'classnames'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import { IMap, IOrgObj, IOrganizationTeamMember, IOrganizationTeamMemberUpdateDTO } from '../../../types'
import FormikCheckbox from '../../ui/formik/Checkbox'
import InputWrapper from '../../ui/InputWrapper'
import ImageUpload from 'src/components/ui/ImageUpload'
import FloatingForm from 'src/components/ui/form/FloatingForm'
import UIMessages from 'src/utils/ui-messages'
import Divider from 'src/components/ui/Divider'
import { Input, Select } from '@mobiscroll/react-latest'
import TeamMemberBottomPanel from './TeamMemberBottomPanel'

export interface IFormValues extends IFormValuesSubmit {
  formError?: string
}

export interface IFormValuesSubmit {
  email: string
  username: string
  firstName: string
  lastName: string
  phoneNumber: string
  roles: string[]
  organizationId: string
  roleIds: string[]
}

interface IFormProps {
  completed: boolean
  fetching: boolean
  formRef: any
  isEdit: boolean
  organizations: IMap<IOrgObj>
  processing: boolean
  selectedOrg?: IOrgObj
  teamMember?: IOrganizationTeamMember | any
  onCancelClick: () => void
  onSubmit: (teamMember: IFormValuesSubmit) => Promise<any>
  onSubmitClick: () => void
  onUpdate: (teamMember: IOrganizationTeamMemberUpdateDTO) => Promise<any>
}

const InnerForm = (
  isNew: boolean,
  organizations: IMap<IOrgObj>,
  selectedOrgId: string,
  imageUploadComp: any,
  processing: boolean,
  fetching: boolean,
  completed: boolean,
  selectOrg: (e: any) => void,
  onCancelClick: () => void,
  onSubmitClick: () => void
) => (props: IFormProps & FormikProps<IFormValues>) => {
  const { errors, touched, values, handleBlur, handleChange, handleSubmit, setFieldTouched, setFieldValue } = props
  const orgOptions = Object.values(organizations).map(o => ({ text: o.name, value: o.id }))
  const dropDownValue = organizations[selectedOrgId] ? selectedOrgId : Object.keys(organizations)[0]

  const handlePhoneField = (value: any) => {
    setFieldTouched('phoneNumber')
    setFieldValue('phoneNumber', value)
  }

  const roles = ['ORGANIZATION_ADMIN', 'ORGANIZATION_DISPATCHER']

  const getChecked = (value: any) => {
    const userRoleIDs = values.roles.map((it: any) => it)
    return userRoleIDs.includes(value)
  }

  const getRoleDisplay = (value: string) => {
    if (value === 'ORGANIZATION_ADMIN') {
      return 'Admin'
    } else {
      return 'Dispatcher'
    }
  }

  const {
    email: emailLabel,
    firstName: firstNameLabel,
    lastName: lastNameLabel,
    organization: organizationLabel,
    phoneNumber: phoneNumberLabel,
    username: usernameLabel
  } = UIMessages.teamMember.form

  const inputProps = {
    inputStyle: 'box' as const,
    labelStyle: 'floating' as const,
    theme: 'ios'
  }

  const errorMessage = (message: string) => (
    <span className='font-small weight-300 font-red block relative h0 mt1'>{message}</span>
  )

  const textInput = (value: FieldProps) => {
    const { field } = value

    const inputDetails = {
      username: {
        label: `${usernameLabel}*`,
        required: true,
        type: 'text',
        onBlur: handleBlur,
        onChange: handleChange
      },
      firstName: {
        label: `${firstNameLabel}*`,
        required: true,
        type: 'text',
        onBlur: handleBlur,
        onChange: handleChange
      },
      lastName: {
        label: `${lastNameLabel}*`,
        required: true,
        type: 'text',
        onBlur: handleBlur,
        onChange: handleChange
      },
      email: {
        label: emailLabel,
        required: false,
        type: 'email',
        onBlur: handleBlur,
        onChange: handleChange
      }
    }
    const textInputProps = inputDetails[field.name as keyof typeof inputDetails]
    return <Input {...field} {...inputProps} {...textInputProps} />
  }

  return (
    <Form
      className={`${isNew ? 'tm-personal-info-container' : 'tm-edit-personal-info-container'}`}
      onSubmit={handleSubmit}
    >
      <FloatingForm>
        <div className='mbsc-grid mb3' style={{ padding: '0' }}>
          <div className='mbsc-row'>
            <div className='mbsc-col-12 mbsc-col-lg-8'>
              <p className='bold font-grey3'>{UIMessages.teamMember.personalInfo}</p>
              <Divider />
              <div className='mbsc-row'>
                <div className='mbsc-col-12 mbsc-padding'>{imageUploadComp}</div>
                <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                  <Select
                    {...inputProps}
                    data={orgOptions}
                    label={organizationLabel}
                    value={dropDownValue}
                    onChange={selectOrg}
                  />
                </div>
                <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                  <Field name='username' render={textInput} />
                  {touched.username && errors.username && errorMessage(errors.username)}
                </div>
                <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                  <Field name='firstName' render={textInput} />
                  {touched.firstName && errors.firstName && errorMessage(errors.firstName)}
                </div>
                <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                  <Field name='lastName' render={textInput} />
                  {touched.lastName && errors.lastName && errorMessage(errors.lastName)}
                </div>
                <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                  <Field name='email' render={textInput} />
                  {touched.email && errors.email && errorMessage(errors.email)}
                </div>
                <div className='mbsc-col-12 mbsc-col-lg-6 mbsc-padding'>
                  <PhoneInput
                    className={classNames('phone-input', {
                      'border-red focus-red': !!(touched.phoneNumber && errors.phoneNumber)
                    })}
                    name='phoneNumber'
                    placeholder='Phone number'
                    defaultCountry='US'
                    value={values.phoneNumber}
                    onChange={handlePhoneField}
                    onBlur={handleBlur}
                  />
                  {touched.phoneNumber && errors.phoneNumber && errorMessage(errors.phoneNumber)}
                </div>
              </div>
            </div>
            <div className='mbsc-col-12 mbsc-col-lg-4'>
              <p className='bold font-grey3'>{UIMessages.teamMember.roles}</p>
              <Divider />
              <div className='mbsc-row'>
                <div className='mbsc-col-12 mbsc-padding'>
                  <InputWrapper touched={touched.roles} error={errors.roles}>
                    {roles.map(it => {
                      return (
                        <FormikCheckbox
                          name='roles'
                          key={`role-${it}`}
                          value={it}
                          displayName={getRoleDisplay(it)}
                          checked={getChecked}
                        />
                      )
                    })}
                  </InputWrapper>
                </div>
              </div>
              <p className='bold font-grey3'>{UIMessages.common.tips}</p>
              <Divider />
              <div className='mbsc-row'>
                <div className='mbsc-col-12 mbsc-padding'>
                  <div className='font-light-grey'>{UIMessages.teamMember.tipsAboutTeamMembers}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </FloatingForm>
      <TeamMemberBottomPanel
        completed={completed}
        fetching={fetching}
        processing={processing}
        onCancelClick={onCancelClick}
        onSubmitClick={onSubmitClick}
      />
    </Form>
  )
}

class TeamMemberInnerForm extends React.Component<IFormProps> {
  public state = {
    selectedOrgId: this.props.selectedOrg ? this.props.selectedOrg.id : ''
  }

  private compDivRef: any = null

  private validationSchema = Yup.object().shape({
    username: Yup.string().required('Username is required'),
    email: Yup.string().email('Invalid email address'),
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
    phoneNumber: Yup.string()
      .nullable(true)
      .test('phone-number', 'Phone number is not valid', phoneNumber =>
        phoneNumber ? isValidPhoneNumber(phoneNumber) : true
      )
      // .required('Phone number is required')
      .max(15, 'Phone number is too long'),
    roles: Yup.array().min(1, 'At least one role is required')
  })

  constructor(props: any) {
    super(props)
    this.compDivRef = React.createRef()
  }

  public componentDidUpdate(prevProps: IFormProps) {
    if (this.props.selectedOrg && JSON.stringify(this.props.selectedOrg) !== JSON.stringify(prevProps.selectedOrg)) {
      this.setState({
        selectedOrgId: this.props.selectedOrg.id
      })
    }
  }

  public render() {
    const {
      completed,
      fetching,
      formRef,
      organizations,
      processing,
      selectedOrg,
      teamMember,
      onCancelClick,
      onSubmitClick
    } = this.props

    const teamMemberUserId = teamMember ? teamMember.userId : null
    const firstName = teamMember && teamMember.firstName ? teamMember.firstName + ' ' : ''
    const lastName = teamMember && teamMember.lastName ? teamMember.lastName : ''

    /* Avatar */
    const comp = <ImageUpload name={firstName + lastName} entityId={teamMemberUserId} ref={this.compDivRef} />
    /* Avatar */
    return (
      <Formik
        ref={formRef}
        enableReinitialize={true}
        initialValues={{
          email: teamMember ? teamMember.email : '',
          username: teamMember ? teamMember.username : '',
          firstName: teamMember ? teamMember.firstName : '',
          lastName: teamMember ? teamMember.lastName : '',
          phoneNumber: teamMember && teamMember.phone !== '' ? '+' + teamMember.countryCode + teamMember.phone : '',
          roles: teamMember ? teamMember.roles : []
        }}
        validationSchema={this.validationSchema}
        onSubmit={this.handleSubmit}
        render={InnerForm(
          !teamMember,
          organizations,
          this.state.selectedOrgId,
          comp,
          processing,
          fetching,
          completed,
          this.selectOrg,
          onCancelClick,
          onSubmitClick
        )}
      />
    )
  }
  private selectOrg = (e: any) => {
    this.setState({ selectedOrgId: e ? e.value : '' })
  }

  private handleSubmit = ({ formError, ...teamMember }: IFormValues, { setSubmitting, setFieldError }: any) => {
    // const jsonStringArray = teamMember.roles.map(role => JSON.stringify(role))
    // const parsedRoles = jsonStringArray.map(roleStr => JSON.parse(roleStr))

    const roleObjects = [
      {
        id: '0d9a4bab-e25a-49e3-9e26-6765fcade838',
        type: 'ORGANIZATION',
        roleName: 'ORGANIZATION_ADMIN',
        name: 'Admin'
      },
      {
        id: '5cdba283-f16a-4898-9184-946c216b4557',
        type: 'ORGANIZATION',
        roleName: 'ORGANIZATION_DISPATCHER',
        name: 'Dispatcher'
      }
    ]

    const parsedIds = teamMember.roles.map(role => {
      const roleObject = roleObjects.find(roleObject => roleObject.roleName === role)

      if (roleObject !== undefined) {
        return roleObject.id
      } else {
        return ''
      }
    })

    const roleNames = teamMember.roles
    if (this.props.isEdit) {
      this.props
        .onUpdate({
          id: this.props.teamMember.id,
          username: teamMember.username,
          firstName: teamMember.firstName,
          lastName: teamMember.lastName,
          roles: roleNames,
          phoneNumber: teamMember.phoneNumber,
          email: teamMember.email,
          countryCode: this.props.teamMember.countryCode,
          organizationId: this.state.selectedOrgId,
          roleIds: parsedIds
        })
        .catch((error: string) => {
          setFieldError('formError', 'There was a problem with your request')
          setSubmitting(false)
        })
    } else {
      this.props
        .onSubmit({
          ...teamMember,
          organizationId: this.state.selectedOrgId,
          roleIds: parsedIds
        })
        .then((obj: any) => {
          this.compDivRef.current.submit(obj.id).then(() => {
            setSubmitting(false)
          })
        })
        .catch((error: string) => {
          setFieldError('formError', 'There was a problem with your request')
          setSubmitting(false)
        })
    }
  }
}

export default TeamMemberInnerForm



// WEBPACK FOOTER //
// ./src/components/team-members/form/TeamMemberInnerForm.tsx